import { useState } from "react";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import DeleteIcon from "@mui/icons-material/Delete";
import IconButton from '@mui/material/IconButton';
import Typography from "@mui/material/Typography";

import { deleteApiKey, type ApiKey as ApiKeyProps } from "api/services/ApiKey";
import { useQueryClient } from "@tanstack/react-query";

type Props = {
  apiKey: ApiKeyProps;
};

export default function ApiKeyCard({ apiKey }: Props) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const queryClient = useQueryClient();

  const onSuccess = () => {
    queryClient.invalidateQueries({ queryKey: [`client-apikeys-${apiKey.client_id}`] });
    setConfirmOpen(false);
  };

  const { mutate: deleteKey } = deleteApiKey(String(apiKey.client_id), apiKey.id, onSuccess);

  return (
    <Card variant={"outlined"}>
      <CardContent sx={{ padding: "1em", "&:last-child": { paddingBottom: "1em" } }}>
        <Box display={"flex"} alignItems={"center"}>
          <Typography variant={"overline"} fontWeight={"bold"} flex={1}>
            {apiKey.id}
          </Typography>
          <Chip label={apiKey.role} color={"primary"} size={"small"} />
          <IconButton onClick={() => setConfirmOpen(true)} sx={{ marginLeft: 1 }}>
            <DeleteIcon fontSize={"small"} />
          </IconButton>
        </Box>
        <Box display={"flex"} columnGap={2}>
          <Chip
            label={apiKey.ess_id === 0 ? "All assets" : apiKey.ess_id}
            color={"secondary"}
            size={"small"}
          />
        </Box>
      </CardContent>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Confirm deletion</DialogTitle>
        <DialogContent>Are you sure you want to delete this API key?</DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button onClick={() => deleteKey({})} color={"error"} variant={"contained"}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}


